import { EditOutlined, EyeOutlined } from '@ant-design/icons'
import { ColumnType } from 'antd/es/table'
import { MenuProps } from 'antd/lib'
import dayjs from 'dayjs'

import {
  GetPregnancyMonitoringListResponseItem,
  PregnancyLocalization,
  PregnancyType
} from 'src/api/requests'
import { TableActionButtons } from 'src/components'
import { dayMonthYearBackSlash } from 'src/libs'
import { useLocales } from 'src/locales'

import { PregnancyStatusTag } from '../components'

type UsePregnancyColumnsProps = {
  showPerson?: boolean
  onOpenDetails?: (id: string) => void
  onEdit?: (id: string) => void
}

export const usePregnancyColumns = ({
  showPerson,
  onOpenDetails,
  onEdit
}: UsePregnancyColumnsProps) => {
  const { t } = useLocales()

  const PersonLocalizationStatus = {
    [PregnancyLocalization.INTRAUTERINE]: t(
      'pregnancy.pregnant_localization_status.intrauterine'
    ),
    [PregnancyLocalization.EXTRAUTERINE]: t(
      'pregnancy.pregnant_localization_status.extrauterine'
    )
  }

  const pregnancyTypeMainOptions = Object.values(PregnancyType).map((item) => ({
    value: item,
    label: t(`pregnancy.pregnancy_type_status.${item}`)
  }))

  const getItems = (id: string): MenuProps['items'] => {
    const items: MenuProps['items'] = []

    if (onOpenDetails) {
      items.push({
        key: 'see_details',
        label: t('common.see_details'),
        icon: <EyeOutlined />,
        onClick: () => onOpenDetails(id)
      })
    }

    if (onEdit) {
      items.push({
        key: 'edit',
        label: t('common.edit'),
        icon: <EditOutlined />,
        onClick: () => onEdit(id)
      })
    }

    return items
  }

  const columns: ColumnType<GetPregnancyMonitoringListResponseItem>[] = [
    {
      key: 'lastMenstrualPeriod',
      title: `${t('pregnancy.DUM')}`,
      render: (_, { lastMenstrualPeriod }) =>
        lastMenstrualPeriod
          ? dayjs(lastMenstrualPeriod).format(dayMonthYearBackSlash)
          : '-'
    },
    {
      key: 'pregnancy_type',
      title: t('pregnancy.pregnancy_type'),
      render: (_, { pregnancyType }) =>
        pregnancyTypeMainOptions?.find(
          (option) => option.value === pregnancyType
        )?.label ?? '-'
    },
    {
      key: 'pregnancy_localization',
      title: t('pregnancy.pregnancy_localization'),
      render: (_, { pregnancyLocalization }) =>
        pregnancyLocalization
          ? PersonLocalizationStatus[pregnancyLocalization]
          : '-'
    },
    {
      key: 'created',
      title: `${t('pregnancy.registration_date')}`,
      render: (_, { created }) =>
        created ? dayjs(created).format(dayMonthYearBackSlash) : '-'
    },
    ...(showPerson
      ? [
          {
            key: 'last name',
            dataIndex: ['person', 'lastName'],
            title: t('form.last_name')
          },
          {
            key: 'first name',
            dataIndex: ['person', 'firstName'],
            title: t('form.first_name')
          }
        ]
      : []),

    {
      key: 'gestationalAgeInWeeks',
      title: t('pregnancy.age'),
      dataIndex: 'gestationalAgeInWeeks'
    },
    {
      key: 'lastMenstrualPeriod',
      title: `${t('pregnancy.estimated_birth_date')}`,
      render: (_, { expectedDateOfBirth }) =>
        expectedDateOfBirth
          ? dayjs(expectedDateOfBirth).format(dayMonthYearBackSlash)
          : '-'
    },

    {
      key: 'status',
      title: t('analysis.status'),
      render: (_, { pregnancyActionStatus }) => (
        <PregnancyStatusTag status={pregnancyActionStatus} />
      )
    },
    {
      key: 'registration_date',
      title: `${t('pregnancy.registration_date')}`,
      render: (_, { expectedDateOfBirth }) =>
        expectedDateOfBirth
          ? dayjs(expectedDateOfBirth).format(dayMonthYearBackSlash)
          : '-'
    },
    {
      key: 'actions',
      render: ({ id }) => (
        <TableActionButtons justify="flex-end" items={getItems(id)} />
      )
    }
  ]

  return {
    columns
  }
}
